import { FC } from 'react'
import { TFunction } from 'react-i18next'

import { Box, Chip, Tooltip, Typography, alpha } from '@mui/material'

import { GRTableColumn, SortOrder } from '../../../components/GRTable/GRTable'
import { MotivationSource, ObjectId, TrackedGame } from '../types'
import { clickableProps } from './interactive'
import { useMotivationSourceColors } from './motivationSourceColors'
import { gameIconSx, linkTypographySx } from './styles'
import { isUnknownGenre } from './tagColumns'
import { UATaxonomyLabels } from './taxonomyLabels'

/**
 * Shared `GRTable` pieces for the `TrackedGame` listings (`AllGamesDialog` and
 * `CompareGameDialog`). Both dialogs render the same underlying rows, so the
 * genre and motivation-availability cells live here to keep them in sync; the
 * dialogs keep their own game-name column because their link semantics differ
 * (in-app navigation vs a real anchor that supports open-in-new-tab).
 */

/**
 * Square app icon with the store-style rounded corners used across the listings.
 * Renders a neutral tile of the same size when the game has no icon on file.
 */
export const GameIconThumb: FC<{ src?: string; alt: string }> = ({ src, alt }) =>
  src ? (
    <Box component="img" src={src} alt={alt} sx={gameIconSx(44, { border: false })} />
  ) : (
    <Box role="img" aria-label={alt} sx={gameIconSx(44, { border: false })} />
  )

/**
 * Per-source availability chip. Available profiles get the source's brand colour
 * (matching the radar/bar chart legend); missing ones fall back to a faded,
 * struck-through chip so the row still groups the two sources visually while
 * making the missing-data state unambiguous.
 */
const MotivationAvailabilityChip: FC<{ available: boolean; source: MotivationSource; sourceLabel: string; t: TFunction }> = ({
  available,
  source,
  sourceLabel,
  t,
}) => {
  const sourceColors = useMotivationSourceColors()
  const stroke = sourceColors[source].stroke
  const tooltip = available ? t('motivation_available_yes', { source: sourceLabel }) : t('motivation_available_no', { source: sourceLabel })

  return (
    <Tooltip title={tooltip} arrow>
      <Chip
        label={sourceLabel}
        size="small"
        aria-label={tooltip}
        sx={{
          height: 22,
          fontSize: 12,
          fontWeight: 'bold',
          color: available ? stroke : 'text.disabled',
          backgroundColor: available ? alpha(stroke, 0.14) : 'action.hover',
          border: '1px solid',
          borderColor: available ? alpha(stroke, 0.4) : 'divider',
          opacity: available ? 1 : 0.6,
          textDecoration: available ? 'none' : 'line-through',
          '& .MuiChip-label': { px: 1 },
        }}
      />
    </Tooltip>
  )
}

/** The Game & Live Events / Creative Ads availability chip pair for one row. */
export const MotivationAvailabilityChips: FC<{ row: TrackedGame; t: TFunction }> = ({ row, t }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, justifyContent: 'center', whiteSpace: 'nowrap' }}>
    <MotivationAvailabilityChip available={row.hasGameDesignMotivations} source="gameDesign" sourceLabel={t('motivation_source_game_design')} t={t} />
    <MotivationAvailabilityChip available={row.hasCreativeAdsMotivations} source="creativeAds" sourceLabel={t('motivation_source_creative_ads')} t={t} />
  </Box>
)

/**
 * Motivation-availability column. Sorted by the count of available profiles so
 * games with both series bubble to the top, then single-source rows, then none.
 * Ties break toward rows that have the Game & Live Events series.
 */
export const motivationAvailabilityColumn = (t: TFunction): GRTableColumn<TrackedGame, undefined> => ({
  labelAccessor: t('col_motivations'),
  accessor: ({ row }) => <MotivationAvailabilityChips row={row} t={t} />,
  sortAccessor: ({ row }) => (row.hasGameDesignMotivations ? 2 : 0) + (row.hasCreativeAdsMotivations ? 1 : 0),
  sortable: true,
  width: 240,
  cellProps: { align: 'center' },
  headerCellProps: { align: 'center', sx: { fontWeight: 'bold' } },
})

/**
 * Genre column. Pass `onGenreClick` to make the genre pivot into the genre
 * drill-down; without it the genre renders as plain text.
 */
export const trackedGameGenreColumn = (
  t: TFunction,
  genreLabel: UATaxonomyLabels['genreLabel'],
  onGenreClick?: (genreId: ObjectId) => void
): GRTableColumn<TrackedGame, undefined> => ({
  labelAccessor: t('col_genre'),
  accessor: ({ row }) =>
    isUnknownGenre(row.genre) ? (
      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
        {t('common:not_available_shorthand')}
      </Typography>
    ) : onGenreClick ? (
      <Typography variant="body2" sx={linkTypographySx} {...clickableProps(() => onGenreClick(row.genre.id))}>
        {genreLabel(row.genre)}
      </Typography>
    ) : (
      <Typography variant="body2">{genreLabel(row.genre)}</Typography>
    ),
  sortAccessor: ({ row }) => (isUnknownGenre(row.genre) ? '' : genreLabel(row.genre)),
  sortable: true,
  cellProps: { align: 'left' },
  headerCellProps: { align: 'left', sx: { fontWeight: 'bold' } },
})

/** Count column linking back into the game (creatives / unique tags). */
export const trackedGameCountColumn = (
  label: string,
  field: 'creativesCount' | 'uniqueTagsCount',
  onGameClick: (gameId: ObjectId) => void,
  opts?: { sortOrder?: SortOrder }
): GRTableColumn<TrackedGame, undefined> => ({
  labelAccessor: label,
  accessor: ({ row }) => (
    <Typography variant="body2" sx={linkTypographySx} {...clickableProps(() => onGameClick(row.id))}>
      {row[field]}
    </Typography>
  ),
  sortAccessor: field,
  sortable: true,
  ...(opts?.sortOrder ? { sortOrder: opts.sortOrder } : {}),
  width: 140,
  cellProps: { align: 'center' },
  headerCellProps: { align: 'center', sx: { fontWeight: 'bold' } },
})
